/**
 * Error handling utilities for SSE metrics injection
 */

import { QueryClient } from '@tanstack/react-query';
import type { BackendMetricsData } from '../types/metricsTypes';

/**
 * Fallback strategy when direct cache injection fails
 */
export const fallbackToInvalidation = (queryClient: QueryClient, error?: Error, timezone?: string) => {
  console.warn('🔄 Falling back to traditional cache invalidation due to error:', error?.message || 'Unknown error');
  
  const effectiveTimezone = timezone || 'UTC';
  
  // Invalidate summary data that should have been injected
  queryClient.invalidateQueries({ 
    queryKey: ['accountSummary'],
    refetchType: 'active'
  });
  queryClient.invalidateQueries({ 
    queryKey: ['strategySummary'],
    refetchType: 'active'
  });
  queryClient.invalidateQueries({ 
    queryKey: ['accountBalances'],
    refetchType: 'active'
  });
  queryClient.invalidateQueries({ 
    queryKey: ['pnlTimeframes', effectiveTimezone],
    refetchType: 'active'
  });
  
  // Trade-related widgets now use shared caches - invalidating closedTrades covers both page and widget
  queryClient.invalidateQueries({ 
    queryKey: ['closedTrades'],
    refetchType: 'active'
  });
};

/**
 * Enhanced error logging for debugging
 */
export const logMetricsError = (phase: string, error: Error, context?: any) => {
  console.error(`❌ SSE Metrics Error [${phase}]:`, {
    message: error.message,
    stack: error.stack,
    context,
    timestamp: new Date().toISOString()
  });
  
  // Send to monitoring service if available
  if (typeof window !== 'undefined' && (window as any).analyticsReporter) {
    (window as any).analyticsReporter.captureException(error, {
      tags: { component: 'sse-metrics', phase },
      extra: context
    });
  }
};

/**
 * Retry mechanism for cache injection
 */
export const retryWithDelay = async (
  fn: () => Promise<void>,
  maxAttempts: number = 3,
  delay: number = 1000
): Promise<boolean> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await fn();
      if (attempt > 1) {
        console.log(`✅ Cache injection succeeded on attempt ${attempt}`);
      }
      return true;
    } catch (error) {
      console.warn(`⚠️ Cache injection attempt ${attempt} failed:`, error);
      
      if (attempt === maxAttempts) {
        console.error(`❌ Cache injection failed after ${maxAttempts} attempts`);
        return false;
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
  
  return false;
};

/**
 * Validate cache data integrity after injection
 * Now supports both incremental and full update validation
 */
export const validateCacheIntegrity = (
  queryClient: QueryClient, 
  metricsData: BackendMetricsData, 
  timezone?: string,
  updateType: 'incremental' | 'full_refresh' = 'full_refresh'
): boolean => {
  try {
    // Get effective timezone for timeframes query key
    const effectiveTimezone = timezone || 'UTC';
    
    // Check if data was properly injected
    const accountSummary = queryClient.getQueryData(['accountSummary']);
    const strategySummary = queryClient.getQueryData(['strategySummary']);
    const accountBalances = queryClient.getQueryData(['accountBalances']);
    const pnlTimeframes = queryClient.getQueryData(['pnlTimeframes', effectiveTimezone]);
    
    if (updateType === 'incremental') {
      // ✅ INCREMENTAL: Just check that cache exists and timeframes are updated
      const basicDataExists = accountSummary !== undefined && 
                             strategySummary !== undefined && 
                             accountBalances !== undefined && 
                             pnlTimeframes !== undefined;
      
      if (basicDataExists) {
        console.log('✅ Incremental cache integrity validation passed - all caches exist and timeframes updated');
        return true;
      } else {
        console.warn('🔍 Incremental cache integrity check failed - some caches missing:', {
          hasAccountSummary: accountSummary !== undefined,
          hasStrategySummary: strategySummary !== undefined,
          hasAccountBalances: accountBalances !== undefined,
          hasTimeframes: pnlTimeframes !== undefined
        });
        return false;
      }
    } else {
      // ✅ FULL REFRESH: Check counts match exactly (legacy validation)
      const accountsMatch = Array.isArray(accountSummary) && accountSummary.length === metricsData.accounts.length;
      const strategiesMatch = Array.isArray(strategySummary) && strategySummary.length === metricsData.strategies.length;
      const balancesExist = accountBalances !== undefined;
      const timeframesExist = pnlTimeframes !== undefined;
      
      const isValid = accountsMatch && strategiesMatch && balancesExist && timeframesExist;
      
      if (!isValid) {
        console.warn('🔍 Full refresh cache integrity check failed:', {
          accountsMatch,
          strategiesMatch,
          balancesExist,
          timeframesExist,
          expectedAccounts: metricsData.accounts.length,
          expectedStrategies: metricsData.strategies.length,
          actualAccounts: Array.isArray(accountSummary) ? accountSummary.length : 0,
          actualStrategies: Array.isArray(strategySummary) ? strategySummary.length : 0,
          timeframesData: pnlTimeframes
        });
      } else {
        console.log('✅ Full refresh cache integrity validation passed - all data injected successfully');
      }
      
      return isValid;
    }
  } catch (error) {
    console.error('❌ Error during cache integrity validation:', error);
    return false;
  }
};

/**
 * Performance monitoring for cache injection
 */
export const measureCacheInjectionPerformance = <T>(
  operation: string,
  fn: () => T
): T => {
  const startTime = performance.now();
  
  try {
    const result = fn();
    const duration = performance.now() - startTime;
    
    console.log(`⚡ ${operation} completed in ${duration.toFixed(2)}ms`);
    
    // Log slow operations
    if (duration > 100) {
      console.warn(`🐌 Slow ${operation}: ${duration.toFixed(2)}ms`);
    }
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    console.error(`❌ ${operation} failed after ${duration.toFixed(2)}ms:`, error);
    throw error;
  }
};